import * as React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { Stack, Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { saveAs } from "file-saver";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import dayjs from "dayjs"; // For date formatting

const API_URL = process.env.REACT_APP_API_URL;

const CardDashboard = (props) => {
  const navigation = useNavigate();
  const [currentStatus, setCurrentStatus] = React.useState(null);
  const [currentStage, setCurrentStage] = React.useState(null);
  const [comment, setComment] = React.useState(null);

  const handleDownload = async () => {
    const backendUrl =
      localStorage.getItem("role") === "ADMIN"
        ? `${API_URL}/api/admin/report/${props.currentYear}/${props.currentQuarter}/download`
        : `${API_URL}/api/report/${props.currentYear}/${props.currentQuarter}/download`;
    axios
      .get(backendUrl, {
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("jwt")} `,
        },
      })
      .then((response) => {
        const blob = new Blob([response.data], { type: "application/zip" });
        saveAs(blob, "downloaded.zip");
      })
      .catch((error) => {
        console.error("Error downloading zip file:", error);
      });
  };

  React.useEffect(() => {
    const requestOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("jwt")} `,
      },
    };
    fetch(`${API_URL}/api/report/${props.id}/steps`, requestOptions)
      .then((response) => response.json())
      .then((data) => {
        if (data[0]) {
          setCurrentStatus(data[0].status);
          if (data[0].comment) {
            setComment(data[0].comment);
            setCurrentStage(data[0].currentStage);
          }
        } else {
          setCurrentStatus("New");
        }
      });
  }, [props.id]);

  const handleGenerateReport = async () => {
    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("jwt")} `,
        },
      };
      const response = await fetch(
        `${API_URL}/api/admin/report/generateReport/${props.currentYear}/${props.currentQuarter}`,
        requestOptions
      );
      if (!response.ok) throw new Error("Failed to generate report");
      const data = await response.json();
      console.log("Report generated successfully:", data);
    } catch (error) {
      console.error("Error generating report:", error);
    }
  };

  return (
    <Card
      sx={{
        minWidth: 275,
        backgroundColor:
          props.stage === "Closed" || props.stage === "Completed"
            ? "#14475D"
            : "#067094",
        boxShadow: "0 4px 6px #FFFFFF",
        transition: "transform 0.3s, box-shadow 0.3s",
        "&:hover": {
          transform: "scale(1.05)",
          boxShadow: "0 8px 12px #FFFFFF",
        },
      }}
    >
      <CardContent>
        <Typography
          variant="h5"
          component="div"
          sx={{
            fontFamily: "Montserrat, sans-serif",
            textTransform: "lowercase",
            "&:first-letter": {
              textTransform: "capitalize",
            },
            fontWeight: "bold",
            color: "#FFFFFF",
          }}
        >
          QUARTER
        </Typography>
        <Typography
          variant="h1"
          sx={{
            mb: 1.5,
            fontFamily: "Montserrat, sans-serif",
            fontWeight: "bold",
            background: "linear-gradient(to bottom, #2B596D,#FFFFFF)",
            WebkitBackgroundClip: "text",
            color: "transparent",
            display: "inline-block",
          }}
          color="text.secondary"
        >
          0{props.index}
        </Typography>
        <Stack direction="row" spacing={10}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Montserrat, sans-serif",
              color: "#FFFFFF",
            }}
          >
            {props.stage === "Awaiting" ? currentStatus : props.stage}
            <br />
          </Typography>
          <Stack spacing={1}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Montserrat, sans-serif",
                color: "#FFFFFF",
              }}
            >
              {props.currentQuarter}
            </Typography>
            {/* Show deadline only for the current quarter (isEdit true) */}
            {props.isEdit && props.deadline && (
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Montserrat, sans-serif",
                  color: "#FFFFFF",
                }}
              >
                Deadline: {dayjs(props.deadline).format("MMM DD, YYYY HH:mm")}
              </Typography>
            )}
            <Stack direction="row" spacing={3}>
              {localStorage.getItem("role") === "ADMIN" && (
                <Box
                  sx={{
                    border: 1,
                    borderRadius: "3px",
                    padding: "2px",
                    borderColor: props.isEdit ? "#FFFFFF" : "#4C4E4E",
                    cursor: props.isEdit ? "pointer" : "default",
                  }}
                  onClick={props.isEdit ? props.onDateClick : undefined}
                >
                  <CalendarTodayIcon
                    style={{ color: props.isEdit ? "#FFFFFF" : "#4C4E4E" }}
                  />
                </Box>
              )}
              {props.isEdit ? (
                <Box
                  sx={{
                    border: 1,
                    borderRadius: "3px",
                    padding: "2px",
                    borderColor: "#FFFFFF",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    if (localStorage.getItem("role") === "REPRESENTATIVE") {
                      navigation(
                        `/sec/${props.id}/${props.currentYear}/${props.currentQuarter}`
                      );
                    } else if (localStorage.getItem("role") === "HOD") {
                      navigation(
                        `/hodEdit/${props.id}/${props.currentYear}/${props.currentQuarter}`
                      );
                    } else if (localStorage.getItem("role") === "ADMIN") {
                      navigation(
                        `/AdminEdit/${props.currentYear}/${props.currentQuarter}`
                      );
                    }
                  }}
                >
                  <EditIcon style={{ color: "#FFFFFF" }} />
                </Box>
              ) : (
                <Box
                  sx={{
                    border: 1,
                    borderRadius: "3px",
                    padding: "2px",
                    borderColor: "#4C4E4E",
                  }}
                >
                  <EditIcon style={{ color: "#4C4E4E" }} />
                </Box>
              )}
              {props.isDownload ? (
                <Box
                  sx={{
                    border: 1,
                    borderRadius: "3px",
                    padding: "2px",
                    borderColor: "#FFFFFF",
                    cursor: "pointer",
                  }}
                  onClick={handleDownload}
                >
                  <DownloadIcon style={{ color: "#FFFFFF" }} />
                </Box>
              ) : (
                <Box
                  sx={{
                    border: 1,
                    borderRadius: "3px",
                    padding: "2px",
                    borderColor: "#4C4E4E",
                  }}
                >
                  <DownloadIcon style={{ color: "#4C4E4E" }} />
                </Box>
              )}
              {localStorage.getItem("role") === "ADMIN" && props.isReport ? (
                <Box
                  sx={{
                    border: 1,
                    borderRadius: "3px",
                    padding: "2px",
                    borderColor: "#FFFFFF",
                    cursor: "pointer",
                  }}
                  onClick={handleGenerateReport}
                >
                  <FileCopyIcon style={{ color: "#FFFFFF" }} />
                </Box>
              ) : (
                localStorage.getItem("role") === "ADMIN" && (
                  <Box
                    sx={{
                      border: 1,
                      borderRadius: "3px",
                      padding: "2px",
                      borderColor: "#4C4E4E",
                    }}
                  >
                    <FileCopyIcon style={ { color: "#4C4E4E" }} />
                  </Box>
                )
              )}
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CardDashboard;